import { GeoJsonPoint } from "../../../../../common/maphelpers";
import Route from "../../../../../common/Route";
import MapPlugin from "../MapPlugin";
import { Checkbox, FormControl, FormControlLabel, FormGroup, InputLabel, MenuItem, Select, Stack } from "@mui/material";
import LayerSettings from "../../common/LayerSettings";
import { useCallback, useEffect, useState } from "react";
import { useOperationsMap } from "../../OperationsMapProvider";
import { useTranslation } from "react-i18next";
import VehicleRouteFetcher from "../common/VehicleRouteFetcher";

export default class VehiclesLayer extends MapPlugin {

  constructor(map) {
    super("vehicles", map);

    this.defaultSettings = {
      driverFilter: "",
      showRoute: false,
      showTitle: true,
      showDriverName: true,
    };

    this._timeout = 45;
    this._routeFetcher = new VehicleRouteFetcher(this.theme);


    this._timer = null;
    
    if (!map.settings?.plugins?.vehicles?.disableHoverEffect) {

      this.on("click", "vehicles-circles", (ev) => {
        const { properties } = ev.features[0];
        this.emitToProvider("vehicleLayerClick", properties.id);
      });

      this.on("mouseenter", "vehicles-circles", (ev) => {
        this._map.getCanvas().style.cursor = "pointer";
        const { properties } = ev.features[0];
        this.setVehicleHighlight(properties.id);
        this.emitToProvider("vehicleLayerHover", properties.id);
      });

      this.on("mouseleave", "vehicles-circles", (ev) => {
        this._map.getCanvas().style.cursor = "";
        this.setVehicleHighlight();
        this.emitToProvider("vehicleLayerHover", null);
      });

    }

    this.addImage("car-cluster");
    this.addImage("nav-arrow")
  }

  onMount() {
    const p = this.theme.palette;

    this.addModel("car-gray", "car-gray.gltf");
    this.addModel("car-blue", "car-blue.gltf");
    this.addModel("car-orange", "car-orange.gltf");

    this._map.addSource("vehicles", {
      type: "geojson",
      cluster: true,
      clusterRadius: 25,
      data: Route.GeoJson(
        this._ops.vehicles
          .filter(t => this.isValidVehicle(t))
          .map(t => this.convertToFeature(t))
      )
    });

    this._map.addSource("vehicles-route-paths", {
      type: "geojson",
      data: Route.EmptyGeoJson()
    });

    this._map.addLayer({
      type: "line",
      source: "vehicles-route-paths",
      id: "vehicles-route-paths-layer",
      slot: "elevation-2",
      paint: {
        "line-color": ["get", "color"],
        "line-width": 5,
        "line-opacity": 0.5
      },
      layout: {
        "line-cap": "round"
      }
    });

    this._map.addLayer({
      id: "vehicles-circles",
      source: "vehicles",
      slot: "elevation-2",
      type: "circle",
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-pitch-alignment": "map",
        "circle-stroke-width": 2,
        "circle-stroke-color": "#FFF",
        "circle-color": p.divider,
        "circle-radius": 16
      }
    });

    this._map.addLayer({
      id: "vehicles-icon",
      source: "vehicles",
      slot: "elevation-2",
      filter: [
        "all",
        ["!", ["has", "point_count"]],
        ["==", ["get", "moving"], true],
      ],
      type: "symbol",
      layout: {
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "icon-size": 2,
        "icon-image": "nav-arrow",
        "icon-rotate": ["get", "heading"],
        "icon-rotation-alignment": "map",
        "icon-offset": [0, -15]
      },
      paint: {
        "icon-color": this.darkmode ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)",
      }
    });

    this._map.addLayer({
      id: "vehicles-highlight",
      source: "vehicles",
      slot: "elevation-2",
      type: "circle",
      filter: ["==", ["get", "id"], "none"],
      paint: {
        "circle-pitch-alignment": "map",
        "circle-stroke-width": 2,
        "circle-stroke-color": "#FFF",
        "circle-color": p.divider,
        "circle-radius": 16
      }
    });

    this._map.addLayer({
      id: "vehicles-model",
      source: "vehicles",
      slot: "elevation-2",
      type: "model",
      filter: ["!", ["has", "point_count"]],
      layout: {
        "model-id": ["get", "model"],
      },
      paint: {
        "model-type": "location-indicator",
        "model-rotation": ["get", "rotation"],
        "model-cast-shadows": true,
        "model-scale": [
          "interpolate",
          ["exponential", 2], // Base-2 exponential scaling matches Mapbox's zoom logic
          ["zoom"],

          8, [1500, 1500, 1500],
          10, [400, 400, 400],
          12, [80, 80, 80],

          13, [40, 40, 40],
          14, [12, 12, 12],

          15, [7, 7, 7],
          18, [1.25, 1.25, 1.25]

        ]
      }
    });


    this._map.addLayer({
      id: "vehicles-text",
      source: "vehicles",
      slot: "elevation-2",
      filter: ["!", ["has", "point_count"]],
      type: "symbol",
      layout: {
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "text-field": ["get", "title"],
        "text-font": [
          "Open Sans Semibold",
          "Arial Unicode MS Bold"
        ],
        "text-size": 12,
        "text-offset": [0, 1.25],
        "text-anchor": "top"
      },
      paint: {
        "text-halo-color": "#fff",
        "text-halo-width": 2
      }
    });


    this._map.addLayer({
      id: "vehicles-cluster-circles",
      source: "vehicles",
      slot: "elevation-2",
      type: "circle",
      filter: ["has", "point_count"],
      paint: {
        "circle-stroke-width": 2,
        "circle-stroke-color": "#FFF",
        "circle-color": p.divider,
        "circle-radius": 16
      }
    });

    this._map.addLayer({
      id: "vehicles-cluster-text",
      source: "vehicles",
      type: "symbol",
      slot: "elevation-2",
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-font": ["DIN Offc Pro Medium", "Arial Unicode MS Bold"],
        "text-size": 11,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "icon-size": 1.8,
        "icon-image": "car-cluster",
        "icon-offset": [0, -3],
        "text-offset": [0, 0.7]
      },
      paint: {
        "icon-color": p.text.secondary,
        "text-halo-color": "#fff",
        "text-halo-width": 1
      }
    });

    this._timer = setInterval(() => this.onPoll(), 15000);
    this.onPoll();

    if (this.settings.showRoute) {
      this.onUpdateRouteData(this._ops.vehicles);
    } else {
      this._map.setLayoutProperty("vehicles-route-paths-layer", "visibility", "none");
    }

  }

  setVehicleHighlight(vehicleId = "none") {
    this._map.setFilter("vehicles-highlight", ["==", ["get", "id"], vehicleId]);
  }

  async onPoll() {
    this.onUpdateVehicleData(this._ops.vehicles);
  }

  onUnMount() {
    // clear update timer
    clearInterval(this._timer);

    // remove layers
    this._map.removeLayer("vehicles-model");
    this._map.removeLayer("vehicles-text");
    this._map.removeLayer("vehicles-icon");
    this._map.removeLayer("vehicles-circles");
    this._map.removeLayer("vehicles-highlight");
    this._map.removeLayer("vehicles-cluster-circles");
    this._map.removeLayer("vehicles-cluster-text");
    this._map.removeLayer("vehicles-route-paths-layer");

    // remove source
    this._map.removeSource("vehicles");
    this._map.removeSource("vehicles-route-paths");

  }

  onUpdateVehicleData(vehicles) {
    if (!this.destroyed) {


      this._map?.getSource("vehicles")?.setData(
        Route.GeoJson(
          vehicles
            .filter(t => this.isValidVehicle(t))
            .map(t => this.convertToFeature(t))
        )
      );

      this.onUpdateRouteData(vehicles);
    }
  }

  async onUpdateRouteData(vehicles) {
    if (!this.destroyed && this.settings.showRoute) {
      await this._routeFetcher.update(vehicles.filter(t => this.isValidVehicle(t)));

      if (!this.destroyed) {
        this._map?.getSource("vehicles-route-paths")?.setData(
          Route.GeoJson(this._routeFetcher.getRouteGeometries())
        );
      }
    }
  }

  isValidVehicle(vehicle) {
    return (
      !!vehicle?.lastReportedLocation?.timestamp &&
      VehiclesLayer.MinutesSince(vehicle.lastReportedLocation.timestamp) < this._timeout &&
      (this.settings.driverFilter === "" || this.settings.driverFilter === vehicle.driver?.id)
    );
  }

  onUpdateSettings(settings, update) {
    this.onUpdateVehicleData(this.vehicles);

    if (update.showRoute) {
      this.onUpdateRouteData(this._ops.vehicles);
    }

    this._map.setLayoutProperty("vehicles-route-paths-layer", "visibility", !!settings.showRoute ? "visible" : "none");
  }

  convertToFeature(vehicle) {
    const { showTitle, showDriverName } = this.settings;
    const lastLocation = vehicle.lastReportedLocation;
    const moving = typeof lastLocation.coords.heading === "number" && (!vehicle?.fleetUnitData || vehicle?.fleetUnitData?.state?.name === "driving");
    const heading = lastLocation.coords.heading || 0;
    const minutesSince = Math.floor(VehiclesLayer.MinutesSince(lastLocation.timestamp));

    const title = showTitle ? (vehicle?.title || "").replace(/[^\p{L}\p{N}\p{P}\p{Z}^$\n]/gu, '') : "";
    const driverName = showDriverName ? (vehicle?.driver?.name || "").replace(/[^\p{L}\p{N}\p{P}\p{Z}^$\n]/gu, '') : "";
    const mins = !!minutesSince ? `\n(~${minutesSince}m)` : ""

    return GeoJsonPoint(
      [
        lastLocation.coords.longitude,
        lastLocation.coords.latitude,
      ],
      {
        id: vehicle.id,
        title: [title, driverName, mins].filter(t => !!t).join("\n"),
        moving: moving,
        heading: heading,
        rotation: [0, 0, heading + 180],
        model: vehicle?.tasks?.length > 0 ? "car-blue" : !!vehicle.driver?.id ? "car-orange" : "car-gray"
      }
    );
  }


  hasLocation(serviceVehicle) {
    if (!serviceVehicle || !serviceVehicle.lastReportedLocation || !serviceVehicle.lastReportedLocation.coords) return false;

    const coords = serviceVehicle.lastReportedLocation.coords;
    const lat = coords.latitude;
    const lng = coords.longitude;

    return (typeof lat === "number" && lat !== 0) && (typeof lng === "number" && lat !== 0);
  }

  static MinutesSince(ts) {
    if (typeof ts !== "number") return 99999;
    return (Date.now() - ts) / 60000.0;
  }

}

export function VehiclesLayerSettings() {
  const { t } = useTranslation();
  const { operationsMap, drivers } = useOperationsMap();
  const settings = operationsMap.getSettings("vehicles");
  const [driverFilter, setDriverFilter] = useState(settings.driverFilter);
  const [showRoute, setShowRoute] = useState(settings.showRoute);
  const [showTitle, setShowTitle] = useState(settings.showTitle);
  const [showDriverName, setShowDriverName] = useState(settings.showDriverName);
  const [active, setActive] = useState(operationsMap.isPluginActive("vehicles"));


  useEffect(() => {
    const fn = () => {
      setDriverFilter(settings.driverFilter);
      setShowRoute(settings.showRoute);
      setShowDriverName(settings.showDriverName);
      setShowTitle(settings.showTitle);
      setActive(operationsMap.isPluginActive("vehicles"));
    };

    operationsMap.on("reset", fn);
    return () => operationsMap.off("reset", fn);
  }, [operationsMap, settings]);

  // Handle plugin active state
  const onHandleAccordion = useCallback((_, val) => { // TODO handle this better, mount and unmount based on accordion open
    setActive(val);

    if (val) {
      operationsMap.activatePlugin("vehicles");
    } else {
      operationsMap.deactivatePlugin("vehicles");
    }
  }, [operationsMap]);

  const onHandleShowRoute = useCallback((ev) => {
    const val = ev.target.checked;
    setShowRoute(val);
    operationsMap.updateSettings("vehicles", { showRoute: val });
  }, [operationsMap]);

  const onHandleDriver = useCallback((ev) => {
    const val = ev.target.value;
    setDriverFilter(val);
    operationsMap.updateSettings("vehicles", { driverFilter: val });
  }, [operationsMap]);


  const onHandleShowTitle = useCallback((ev) => {
    const val = ev.target.checked;
    setShowTitle(val);
    operationsMap.updateSettings("vehicles", { showTitle: val });
  }, [operationsMap]);

  const onHandleShowDriverName = useCallback((ev) => {
    const val = ev.target.checked;
    setShowDriverName(val);
    operationsMap.updateSettings("vehicles", { showDriverName: val });
  }, [operationsMap]);

  return (
    <LayerSettings title={t("operationsMap:layers:vehicles:title")} open={active} onChange={onHandleAccordion}>
      <Stack spacing={1}>

        <FormControl fullWidth size="small" variant="filled">
          <InputLabel>{t("operationsMap:layers:vehicles:driver")}</InputLabel>
          <Select
            onChange={onHandleDriver}
            value={driverFilter || ""}
          >
            <MenuItem value="">{t("common:all")}</MenuItem>
            {drivers.map((driver, value) => <MenuItem value={driver.id} key={value}>{driver.name}</MenuItem>)}
          </Select>
        </FormControl>

        <FormGroup>
          <FormControlLabel control={<Checkbox checked={showRoute} onChange={onHandleShowRoute} />} label={t("operationsMap:layers:vehicles:showDestinationRoute")} />
          <FormControlLabel control={<Checkbox checked={showTitle} onChange={onHandleShowTitle} />} label={t("operationsMap:layers:vehicles:showTitle")} />
          <FormControlLabel control={<Checkbox checked={showDriverName} onChange={onHandleShowDriverName} />} label={t("operationsMap:layers:vehicles:showDriverName")} />
        </FormGroup>

      </Stack>
    </LayerSettings>
  );
}



